import { useState } from 'react';
import { X, Loader2, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTransferSessionCredit } from '../../hooks/useAPI';

interface TransferCreditModalProps {
  creditId: string;
  sessionsRemaining: number;
  packageName: string;
  onClose: () => void;
  onDone: () => void;
}

export default function TransferCreditModal({
  creditId, sessionsRemaining, packageName, onClose, onDone,
}: TransferCreditModalProps) {
  const { transfer, isLoading } = useTransferSessionCredit();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const { data, error } = await transfer(creditId, email.trim());
    if (error) { setError(error.message || 'Transfer failed'); return; }
    setResult(data);
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
        <div className="bg-background rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#D4F04A] flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-7 h-7 text-[#1A6B4F]" />
          </div>
          <h2 className="text-lg font-semibold">Credit transferred</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {result.sessionsTransferred} {result.sessionsTransferred === 1 ? 'session' : 'sessions'} sent to{' '}
            <span className="text-foreground">{result.recipientEmail}</span>.
          </p>
          <button
            onClick={onDone}
            className="mt-5 w-full h-11 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-background rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">Transfer package</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {sessionsRemaining} {sessionsRemaining === 1 ? 'session' : 'sessions'} of {packageName}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">Recipient Deuce email</div>
            <div className="relative">
              <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="friend@example.com"
                className="w-full h-11 pl-9 pr-3 rounded-xl bg-input-background text-sm focus:outline-none border border-transparent focus:border-[#1A6B4F]"
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              The recipient must already have an Deuce account.
            </div>
          </label>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 flex gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            This is final — you'll lose the remaining sessions on this credit.
          </div>

          <div className="flex gap-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-border hover:bg-secondary text-sm"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={isLoading || !email}
              className="flex-1 h-11 rounded-xl bg-[#1A6B4F] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
